import { useNavigate } from 'react-router-dom';
import { AlertTriangle, CircleDot, PackageX, Trophy } from 'lucide-react';
import { RestaurantDashboard } from '../../../types/restaurant/RestaurantDashboardTypes';
import { DashboardPanel, EmptyState } from './RestaurantDashboardComponents';
import {
  formatDashboardDuration,
  formatDashboardMoney,
} from './restaurantDashboardFormatters';

const RESTAURANT_ROUTES = {
  tables: '/restaurant/operation/tables',
  kitchen: '/restaurant/operation/kitchen',
  reservations: '/restaurant/operation/reservations',
  inventory: '/restaurant/inventory/stock',
};

const Metric = ({
  label,
  value,
  dot,
}: {
  label: string;
  value: number | string;
  dot?: string;
}) => (
  <div className="flex items-center justify-between gap-3 rounded-lg border border-stroke bg-gray-2 px-3 py-2.5 dark:border-strokedark dark:bg-meta-4">
    <span className="inline-flex items-center gap-2 text-sm text-body dark:text-bodydark">
      <i className={`h-2.5 w-2.5 rounded-full ${dot ?? 'bg-primary'}`} />
      {label}
    </span>
    <strong className="text-black dark:text-white">{value}</strong>
  </div>
);

export const DiningRoomPanel = ({
  tables,
}: {
  tables: RestaurantDashboard['tables'];
}) => {
  const navigate = useNavigate();

  const metrics = [
    ['Libres', tables.free, 'bg-success'],
    ['Ocupadas', tables.occupied, 'bg-danger'],
    ['Reservadas', tables.reserved, 'bg-primary'],
    ['En limpieza', tables.cleaning, 'bg-warning'],
    ['Fuera de servicio', tables.outOfService, 'bg-bodydark2'],
  ] as const;

  return (
    <DashboardPanel
      title="Estado del salón"
      subtitle={`${tables.total} mesas registradas · Ahora`}
      action={
        <button
          onClick={() => navigate(RESTAURANT_ROUTES.tables)}
          className="text-xs font-medium text-primary hover:underline"
        >
          Ver mesas
        </button>
      }
    >
      <div className="grid gap-2 p-5 sm:grid-cols-2">
        {metrics.map(([label, value, dot]) => (
          <Metric key={label} label={label} value={value} dot={dot} />
        ))}
      </div>
    </DashboardPanel>
  );
};

export const OperationPanel = ({
  orders,
  kitchen,
}: {
  orders: RestaurantDashboard['orders'];
  kitchen: RestaurantDashboard['kitchen'];
}) => {
  const navigate = useNavigate();

  return (
    <DashboardPanel
      title="Operación"
      subtitle="Órdenes y cola de cocina · Ahora"
      action={
        <button
          onClick={() => navigate(RESTAURANT_ROUTES.kitchen)}
          className="text-xs font-medium text-primary hover:underline"
        >
          Ir a cocina
        </button>
      }
    >
      <div className="grid gap-5 p-5 sm:grid-cols-2">
        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-bodydark2">
            Órdenes activas
          </p>
          <div className="space-y-2">
            <Metric label="Abiertas" value={orders.open} />
            <Metric
              label="Confirmadas"
              value={orders.confirmed}
              dot="bg-success"
            />
            <Metric
              label="En cocina"
              value={orders.inKitchen}
              dot="bg-warning"
            />
            <Metric label="Listas" value={orders.ready} dot="bg-success" />
            <Metric label="En cobro" value={orders.billing} dot="bg-meta-6" />
          </div>
        </div>

        <div>
          <p className="mb-3 text-xs font-semibold uppercase tracking-wide text-bodydark2">
            Cola de cocina
          </p>
          <div className="space-y-2">
            <Metric
              label="Pendientes"
              value={kitchen.pending}
              dot="bg-warning"
            />
            <Metric
              label="En preparación"
              value={kitchen.inPreparation}
              dot="bg-primary"
            />
            <Metric label="Listas" value={kitchen.ready} dot="bg-success" />
            <Metric
              label="Preparación promedio"
              value={formatDashboardDuration(kitchen.averagePreparationMinutes)}
              dot="bg-meta-6"
            />
          </div>
        </div>
      </div>
    </DashboardPanel>
  );
};

export const ReservationsPanel = ({
  reservations,
}: {
  reservations: RestaurantDashboard['reservations'];
}) => {
  const navigate = useNavigate();

  const values = [
    ['Pendientes', reservations.pending],
    ['Confirmadas', reservations.confirmed],
    ['Sentadas', reservations.seated],
    ['Completadas', reservations.completed],
    ['Canceladas', reservations.cancelled],
    ['No se presentaron', reservations.noShow],
  ] as const;

  return (
    <DashboardPanel
      title="Reservaciones de hoy"
      subtitle={`${reservations.totalToday} en total`}
      action={
        <button
          onClick={() => navigate(RESTAURANT_ROUTES.reservations)}
          className="text-xs font-medium text-primary hover:underline"
        >
          Ver agenda
        </button>
      }
    >
      <div className="grid grid-cols-2 gap-2 p-5">
        {values.map(([label, value]) => (
          <Metric key={label} label={label} value={value} />
        ))}
      </div>
    </DashboardPanel>
  );
};

export const TopSellingPanel = ({
  items,
}: {
  items: RestaurantDashboard['topSellingItems'];
}) => (
  <DashboardPanel title="Más vendidos" subtitle="Productos de cobros aceptados">
    {!items.length ? (
      <EmptyState
        icon={<Trophy className="h-6 w-6" />}
        text="Aún no hay productos vendidos en esta fecha."
      />
    ) : (
      <ol className="divide-y divide-stroke px-5 dark:divide-strokedark">
        {items.map((item, index) => (
          <li key={item.menuItemId} className="flex items-center gap-3 py-3.5">
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary/10 text-sm font-bold text-primary">
              {index + 1}
            </span>

            <div className="min-w-0 flex-1">
              <p className="truncate font-medium text-black dark:text-white">
                {item.name}
              </p>
              <p className="text-xs text-bodydark2">{item.quantity} vendidos</p>
            </div>

            <strong className="text-sm text-black dark:text-white">
              {formatDashboardMoney(item.total)}
            </strong>
          </li>
        ))}
      </ol>
    )}
  </DashboardPanel>
);

export const InventoryAlertsPanel = ({
  inventory,
}: {
  inventory: RestaurantDashboard['inventory'];
}) => {
  const navigate = useNavigate();
  const alerts = inventory.alerts.slice(0, 10);

  return (
    <DashboardPanel
      title="Inventario crítico"
      subtitle={`${inventory.outOfStock} agotados · ${inventory.lowStock} con alerta`}
      action={
        <button
          onClick={() => navigate(RESTAURANT_ROUTES.inventory)}
          className="text-xs font-medium text-primary hover:underline"
        >
          Ver inventario
        </button>
      }
    >
      {!alerts.length ? (
        <EmptyState
          icon={<PackageX className="h-6 w-6" />}
          text="No hay materiales con existencias críticas."
        />
      ) : (
        <div className="divide-y divide-stroke px-5 dark:divide-strokedark">
          {alerts.map((alert) => {
            const empty = alert.currentStock <= 0;

            return (
              <button
                key={alert.inventoryId}
                onClick={() => navigate(RESTAURANT_ROUTES.inventory)}
                className="flex w-full items-center gap-3 py-3.5 text-left transition-colors hover:text-primary"
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg ${
                    empty
                      ? 'bg-danger/10 text-danger'
                      : 'bg-warning/15 text-warning'
                  }`}
                >
                  {empty ? (
                    <AlertTriangle className="h-5 w-5" />
                  ) : (
                    <CircleDot className="h-5 w-5" />
                  )}
                </span>

                <div className="min-w-0 flex-1">
                  <p className="truncate font-medium text-black dark:text-white">
                    {alert.name}
                  </p>
                  <p className="text-xs text-bodydark2">
                    {alert.code} · mínimo {alert.minimumStock} {alert.unitName}
                  </p>
                </div>

                <span
                  className={`text-right text-sm font-semibold ${
                    empty ? 'text-danger' : 'text-warning'
                  }`}
                >
                  {alert.currentStock} {alert.unitName}
                  <small className="block font-normal">
                    {empty ? 'Agotado' : 'Stock bajo'}
                  </small>
                </span>
              </button>
            );
          })}
        </div>
      )}
    </DashboardPanel>
  );
};
